// BLOKAS 1: BacktestPeriodModal.tsx - IMPORT STATEMENTS
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

console.log(`📦 [BLOKAS 1 BacktestPeriodModal.tsx] Importuoti moduliai: React, motion, toast`);

// BLOKAS 2: BacktestPeriodModal.tsx - TYPE DEFINITIONS
type SectionId = 'basic' | 'strategy' | 'coins' | 'primary-time' | 'secondary-time' | 'exchanges' | 'backtests';

interface BacktestPeriodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (days: number, customRange?: { from: string; to: string }) => Promise<void>;
  onNavigateToSection?: (s: SectionId) => void;
}

console.log(`📦 [BLOKAS 2 BacktestPeriodModal.tsx] Apibrėžti tipai: SectionId, BacktestPeriodModalProps`);

// BLOKAS 3: BacktestPeriodModal.tsx - CONSTANTS DEFINITION
const CARDS = [
  { big: '1 Week',   small: 'Last 7 days',    days: 7 },
  { big: '1 Month',  small: 'Last 30 days',   days: 30 },
  { big: '2 Months', small: 'Last 60 days',   days: 60 },
  { big: '3 Months', small: 'Last 90 days',   days: 90 },
  { big: '6 Months', small: 'Last 180 days',  days: 180 },
  { big: '1 Year',   small: 'Last 365 days',  days: 365 },
];

console.log(`📦 [BLOKAS 3 BacktestPeriodModal.tsx] Apibrėžta CARDS konstanta su ${CARDS.length} periodų pasirinkimais`);
console.log(`   ↳ Sub-blokas 3.1: CARDS masyvas apibrėžtas su greitais periodų pasirinkimais`);

// BLOKAS 4: BacktestPeriodModal.tsx - MAIN COMPONENT DEFINITION
const BacktestPeriodModal: React.FC<BacktestPeriodModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onNavigateToSection
}) => {
  console.log(`📦 [BLOKAS 4 BacktestPeriodModal.tsx] Komponento inicializacija, isOpen: ${isOpen}`);
  
  // SUB-BLOKAS 4.1: STATE VARIABLES
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  console.log(`   ↳ Sub-blokas 4.1: State kintamieji inicijuoti`);
  console.log(`     • loading: ${loading}, from: "${from}", to: "${to}", error: ${error}`);

  // SUB-BLOKAS 4.2: EARLY RETURN IF NOT OPEN
  if (!isOpen) {
    console.log(`   ↳ Sub-blokas 4.2: Komponentas neaktyvus, grąžinamas null`);
    return null;
  }

  // SUB-BLOKAS 4.3: QUICK PERIOD CLICK HANDLER
  const clickQuick = async (days: number) => {
    console.log(`   ↳ Sub-blokas 4.3: clickQuick funkcija iškviesta, days: ${days}`);
    
    if (loading) {
      console.log(`     • [WARNING] Loading vyksta, funkcija ignoruojama`);
      return;
    }
    
    setLoading(true);
    setError(null);
    console.log(`     • State atnaujintas: loading=true, error=null`);
    
    try {
      console.log(`     • Iškviečiama onConfirm su ${days} dienų`);
      await onConfirm(days);
      console.log(`     • onConfirm sėkmingai įvykdyta`);
    } catch (err: any) {
      console.error(`     • [ERROR] clickQuick klaida:`, err);
      console.log(`     • Sub-blokas 4.3.1: Klaidos apdorojimas`);
      setError(err.message || 'Failed to create backtest');
      console.log(`       • Error state nustatytas: "${err.message || 'Failed to create backtest'}"`);
    } finally {
      setLoading(false);
      console.log(`     • Loading state atstatytas į false`);
    }
  };

  // SUB-BLOKAS 4.4: CUSTOM PERIOD CLICK HANDLER
  const clickCustom = async () => {
    console.log(`   ↳ Sub-blokas 4.4: clickCustom funkcija iškviesta`);
    console.log(`     • from: "${from}", to: "${to}", loading: ${loading}`);
    
    if (loading) {
      console.log(`     • [WARNING] Loading vyksta, funkcija ignoruojama`);
      return;
    }
    
    if (!from || !to) {
      console.log(`     • [VALIDATION] Nėra pasirinktos datos, error state nustatomas`);
      setError('Please select date range');
      return;
    }

    setLoading(true);
    setError(null);
    console.log(`     • State atnaujintas: loading=true, error=null`);

    try {
      console.log(`     • Sub-blokas 4.4.1: Datų konvertavimas ir skaičiavimas`);
      const fromDate = new Date(from);
      const toDate = new Date(to);
      const diffTime = Math.abs(toDate.getTime() - fromDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      console.log(`       • fromDate: ${fromDate}, toDate: ${toDate}`);
      console.log(`       • diffDays apskaičiuota: ${diffDays}`);

      console.log(`     • Iškviečiama onConfirm su ${diffDays} dienų ir custom range`);
      await onConfirm(diffDays, { from, to });
      console.log(`     • onConfirm sėkmingai įvykdyta`);
    } catch (err: any) {
      console.error(`     • [ERROR] clickCustom klaida:`, err);
      console.log(`     • Sub-blokas 4.4.2: Klaidos apdorojimas`);
      setError(err.message || 'Failed to create backtest');
      console.log(`       • Error state nustatytas: "${err.message || 'Failed to create backtest'}"`);
    } finally {
      setLoading(false);
      console.log(`     • Loading state atstatytas į false`);
    }
  };

  console.log(`📦 [BLOKAS 5 BacktestPeriodModal.tsx] JSX renderinimas pradėtas`);
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-[min(92vw,520px)] max-h-[85vh] overflow-auto rounded-xl bg-white dark:bg-gray-800 p-5 shadow-xl"
      >
        {/* SUB-BLOKAS 5.1: HEADER SECTION */}
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Select Backtest Period</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Choose the time period for your backtest. Longer periods provide more comprehensive results but may take longer to process.
        </p>
        
        {/* SUB-BLOKAS 5.2: QUICK SELECT GRID */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          {CARDS.map((c, index) => (
            <button
              key={c.days}
              onClick={() => {
                console.log(`   ↳ Sub-blokas 5.2.${index + 1}: Paspaustas mygtukas "${c.big}" (${c.days} days)`);
                clickQuick(c.days);
              }}
              className="rounded-lg border border-gray-200 dark:border-gray-600 p-4 text-left transition hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-[.99] disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              <div className="text-base font-semibold text-gray-900 dark:text-white">{c.big}</div>
              <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{c.small}</div>
            </button>
          ))}
        </div>

        {/* SUB-BLOKAS 5.3: CUSTOM RANGE SECTION */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          {/* SUB-BLOKAS 5.3.1: ALL DATA CARD */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-600 p-4">
            <div className="text-base font-semibold text-gray-900 dark:text-white">All Available Data</div>
            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Maximum historical data</div>
            <button
              className="mt-3 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => {
                console.log(`   ↳ Sub-blokas 5.3.1: Paspaustas "Use All Data" mygtukas`);
                clickQuick(36500);
              }}
              disabled={loading}
            >
              Use All Data
            </button>
          </div>

          {/* SUB-BLOKAS 5.3.2: CUSTOM RANGE CARD */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-600 p-4">
            <div className="text-base font-semibold text-gray-900 dark:text-white">Custom Range</div>
            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Select specific dates</div>

            <div className="mt-2 flex items-center gap-2 min-w-0">
              <input
                type="date"
                className="w-full min-w-0 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                value={from}
                onChange={(e) => {
                  console.log(`   ↳ Sub-blokas 5.3.2.1: Nustatyta from data: "${e.target.value}"`);
                  setFrom(e.target.value);
                }}
                disabled={loading}
              />
              <span className="text-gray-400">—</span>
              <input
                type="date"
                className="w-full min-w-0 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                value={to}
                onChange={(e) => {
                  console.log(`   ↳ Sub-blokas 5.3.2.2: Nustatyta to data: "${e.target.value}"`);
                  setTo(e.target.value);
                }}
                disabled={loading}
              />
            </div>

            <div className="mt-3 flex justify-end">
              <button
                className="rounded-md bg-gray-900 dark:bg-gray-700 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => {
                  console.log(`   ↳ Sub-blokas 5.3.2.3: Paspaustas "Run with Range" mygtukas`);
                  clickCustom();
                }}
                disabled={loading || !from || !to}
              >
                {loading ? 'Creating…' : 'Run with Range'}
              </button>
            </div>
          </div>
        </div>

        {/* SUB-BLOKAS 5.4: ERROR DISPLAY */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="text-sm text-red-800 dark:text-red-200">{error}</div>
          </div>
        )}

        {/* SUB-BLOKAS 5.5: CANCEL BUTTON */}
        <div className="mt-5 flex justify-end">
          <button 
            className="rounded-md border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" 
            onClick={() => {
              console.log(`   ↳ Sub-blokas 5.5: Paspaustas Cancel mygtukas`);
              onClose();
            }}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </div>
  );
};

console.log(`📦 [BLOKAS 6 BacktestPeriodModal.tsx] Komponentas eksportuojamas`);
export default BacktestPeriodModal;